export const SPOKEN_TEXT_ATTRS = {
  className: 'scannable-bullet-text',
  lang: 'en-AU',
};

export const AUDIO_ELEMENT_ATTRS = {
  playsInline: true,
  preload: 'none' as const,
  muted: true,
};

export interface WebSpeechPreviewOptions {
  language?: string;
  gender?: string;
  modelId?: string;
  languageTarget?: string;
}

function disableTextTracks(audio: HTMLMediaElement) {
  Array.from(audio.textTracks || []).forEach((track) => {
    track.mode = 'disabled';
  });
}

/** Off-DOM local audio — no DOM node, no crossOrigin, no caption tracks. */
export function createLocalAudio(src: string): HTMLAudioElement {
  const audio = new Audio(src);
  audio.preload = 'auto';
  audio.volume = 1;
  audio.controls = false;
  audio.removeAttribute('crossorigin');
  disableTextTracks(audio);
  return audio;
}

let activeElevenLabsAudio: HTMLAudioElement | null = null;
let audioPlaybackBlocked = false;

export function resetAudioPlaybackGate() {
  audioPlaybackBlocked = false;
}

/** Hard-stop all audio paths — used when ElevenLabs is unavailable. */
export function blockAllAudioPlayback() {
  audioPlaybackBlocked = true;

  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }

  if (activeElevenLabsAudio) {
    activeElevenLabsAudio.pause();
    activeElevenLabsAudio.muted = true;
    activeElevenLabsAudio.src = '';
    activeElevenLabsAudio = null;
  }

  if (typeof document !== 'undefined') {
    document.querySelectorAll('audio').forEach((node) => {
      node.pause();
      node.muted = true;
      node.removeAttribute('src');
      node.load();
    });
  }
}

export function primeAudioElement(audio: HTMLAudioElement | null) {
  if (!audio || audioPlaybackBlocked) return;

  audio.muted = false;
  audio.volume = 1;
  audio.preload = 'auto';
  audio.controls = false;
  audio.removeAttribute('crossorigin');
  disableTextTracks(audio);
}

export async function playManagedAudioBlob(blob: Blob): Promise<HTMLAudioElement> {
  if (audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  if (!blob.size) {
    throw new Error('Empty audio stream');
  }

  const blobType = blob.type || '';
  if (blobType.includes('json') || (blobType.startsWith('text/') && !blobType.includes('audio'))) {
    throw new Error('Invalid audio stream');
  }

  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  primeAudioElement(audio);

  try {
    await new Promise<void>((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Audio decode failed'));
      };
      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
      };

      if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        resolve();
        return;
      }

      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.load();
    });

    await audio.play();
    return audio;
  } catch (error) {
    URL.revokeObjectURL(url);
    audio.pause();
    audio.muted = true;
    audio.src = '';
    throw error;
  }
}

/** Native speechSynthesis is reserved for local voice preview only. */
export function speakScript(_script: string) {
  return;
}

function resolveWebSpeechLang(language?: string): string {
  switch (language) {
    case 'English (Aust)':
      return 'en-AU';
    case 'Spanish':
      return 'es-ES';
    case 'Afrikaans':
      return 'af-ZA';
    default:
      return 'en-US';
  }
}

function pickWebSpeechVoice(
  voices: SpeechSynthesisVoice[],
  language?: string,
  gender?: string,
): SpeechSynthesisVoice | undefined {
  if (!voices.length) return undefined;

  const langPrefix = resolveWebSpeechLang(language).split('-')[0];
  const langMatches = voices.filter((voice) => voice.lang.toLowerCase().startsWith(langPrefix));
  const pool = langMatches.length ? langMatches : voices;

  if (gender === 'Female') {
    return pool.find((voice) => /female|woman|zira|samantha|karen|natasha|sonia|heera/i.test(voice.name))
      ?? pool.find((voice) => !/male|david|mark|james|guy|matthew/i.test(voice.name))
      ?? pool[0];
  }

  if (gender === 'Male') {
    return pool.find((voice) => /male|man|david|mark|james|guy|matthew|george/i.test(voice.name))
      ?? pool[0];
  }

  return pool[0];
}

function loadWebSpeechVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      resolve([]);
      return;
    }

    const synth = window.speechSynthesis;
    const existing = synth.getVoices();
    if (existing.length) {
      resolve(existing);
      return;
    }

    const finish = () => {
      synth.removeEventListener('voiceschanged', finish);
      resolve(synth.getVoices());
    };

    synth.addEventListener('voiceschanged', finish);
    window.setTimeout(finish, 400);
  });
}

export async function speakPreviewWithWebSpeech(
  text: string,
  options: WebSpeechPreviewOptions = {},
): Promise<void> {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    throw new Error('Speech synthesis unavailable');
  }

  window.speechSynthesis.cancel();

  const voices = await loadWebSpeechVoices();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = resolveWebSpeechLang(options.language);
  utterance.rate = 0.95;
  utterance.pitch = options.gender === 'Female' ? 1.05 : 0.95;

  const voice = pickWebSpeechVoice(voices, options.language, options.gender);
  if (voice) utterance.voice = voice;

  await new Promise<void>((resolve, reject) => {
    utterance.onend = () => resolve();
    utterance.onerror = () => reject(new Error('Speech synthesis failed'));
    window.speechSynthesis.speak(utterance);
  });
}

async function playPreviewWithFallback(
  voiceId: string,
  text: string,
  options: WebSpeechPreviewOptions = {},
): Promise<HTMLAudioElement | null> {
  try {
    const { voiceApi } = await import('../services/api');
    const blob = await voiceApi.preview(voiceId, text, options.modelId, options.languageTarget ?? options.language);
    const audio = await playManagedAudioBlob(blob);
    activeElevenLabsAudio = audio;

    audio.onended = () => {
      URL.revokeObjectURL(audio.src);
      audio.src = '';
      if (activeElevenLabsAudio === audio) activeElevenLabsAudio = null;
    };

    return audio;
  } catch (error) {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      await speakPreviewWithWebSpeech(text, options);
      return null;
    }
    throw error;
  }
}

export function cancelElevenLabsPlayback() {
  if (!activeElevenLabsAudio) return;
  activeElevenLabsAudio.pause();
  activeElevenLabsAudio.currentTime = 0;
  activeElevenLabsAudio.muted = true;
  activeElevenLabsAudio.src = '';
  activeElevenLabsAudio = null;
}

export function cancelSpeechPlayback() {
  cancelElevenLabsPlayback();
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}

export async function prepareSpeechAudio(
  voiceId: string,
  script: string,
  languageTarget?: string,
  modelId?: string,
) {
  if (typeof window === 'undefined' || audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  cancelElevenLabsPlayback();

  const { ELEVENLABS_TTS_MODEL } = await import('./voiceProfile');
  const { voiceApi } = await import('../services/api');
  const blob = await voiceApi.preview(
    voiceId,
    script,
    modelId || ELEVENLABS_TTS_MODEL,
    languageTarget,
  );
  return prepareSpeechAudioFromBlob(blob);
}

/** Build a playable Audio element from a prefetched TTS blob. */
export async function prepareSpeechAudioFromBlob(blob: Blob) {
  if (typeof window === 'undefined' || audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  cancelElevenLabsPlayback();

  if (!blob.size) {
    throw new Error('Empty audio stream');
  }

  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  primeAudioElement(audio);

  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      URL.revokeObjectURL(url);
      reject(new Error('Audio load failed'));
    };
    const cleanup = () => {
      audio.removeEventListener('loadedmetadata', onReady);
      audio.removeEventListener('error', onError);
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      resolve();
      return;
    }

    audio.addEventListener('loadedmetadata', onReady, { once: true });
    audio.addEventListener('error', onError, { once: true });
    audio.load();
  });

  activeElevenLabsAudio = audio;
  audio.onended = () => {
    URL.revokeObjectURL(audio.src);
    audio.src = '';
    if (activeElevenLabsAudio === audio) activeElevenLabsAudio = null;
  };

  return audio;
}

export async function speakScriptWithElevenLabs(
  script: string,
  voiceId: string,
  modelId?: string,
  languageTarget?: string,
) {
  if (typeof window === 'undefined' || audioPlaybackBlocked) return;

  cancelElevenLabsPlayback();

  try {
    const { ELEVENLABS_TTS_MODEL } = await import('./voiceProfile');
    const { voiceApi } = await import('../services/api');
    const resolvedModelId = ELEVENLABS_TTS_MODEL;
    const blob = await voiceApi.preview(voiceId, script, resolvedModelId, languageTarget);
    const audio = await playManagedAudioBlob(blob);
    activeElevenLabsAudio = audio;

    audio.onended = () => {
      URL.revokeObjectURL(audio.src);
      audio.src = '';
      if (activeElevenLabsAudio === audio) activeElevenLabsAudio = null;
    };

    return audio;
  } catch (error) {
    throw error;
  }
}

export async function previewVoiceAudio(
  voiceId: string,
  text: string,
  options: WebSpeechPreviewOptions = {},
) {
  if (audioPlaybackBlocked) {
    throw new Error('Audio playback disabled');
  }

  cancelElevenLabsPlayback();
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }

  return playPreviewWithFallback(voiceId, text, options);
}
